import React, { useState } from 'react';
import { Constrain, Stack, Text, Button, Divider, Breakpoint } from '@moda/om';
import LogoModaPrivateIcon from '@moda/icons/logo-moda-private-165-x-12';
import { Link } from 'react-router-dom';
import { hrefFor } from '../../routers';
import { tc } from '../../lib/trackingContext';
import { ModaNetworkRequestModal } from './ModaNetworkRequestModal';

import './ModaNetwork.scss';

export const ModaNetwork: React.FC = () => {
  type ModaNetworkListEvent = 'Moda Network Show Form Clicked';
  const [showRequestModal, setShowRequestModal] = useState(false);

  const handleClick = (text: ModaNetworkListEvent, label: string, location: string) => {
    setShowRequestModal(true);
    tc.track(text, {
      addToTrackingEvent: {
        label: label,
        location: location
      }
    });
  };

  return (
    <>
      <div className="ModaNetwork">
        <Breakpoint gt="sm">
          <Stack
            space={0}
            direction="horizontal"
            className="ModaNetwork__header-container"
            justifyContent="space-between"
          >
            <img
              className="ModaNetwork__image-left"
              src="/dist/public/stylist/moda_network_DT2.jpg"
              alt="Moda Network header left"
            />
            <Stack space={5} className="ModaNetwork__image-center" alignItems="center">
              <Text treatment="h1" family="serif">
                Moda NETWORK
              </Text>

              <Stack space={0}>
                <Text>
                  The world's premier invitation-only network for stylists, interior decorators, and
                  costume designers.
                </Text>
              </Stack>

              <Text>
                Grow your business by joining Moda Network to recieve unique access and commissions
                to luxury brands and designers globally.
              </Text>

              <div className="ModaNetwork__button">
                <Button
                  secondary
                  className="ModaNetwork__button-header"
                  onClick={() =>
                    handleClick(
                      'Moda Network Show Form Clicked',
                      'APPLY',
                      'Header APPLY NOW button'
                    )
                  }
                >
                  APPLY NOW
                </Button>
              </div>
            </Stack>
            <img
              className="ModaNetwork__image-right"
              src="/dist/public/stylist/moda_network_DT3.jpg"
              alt="Moda Network header right"
            />
          </Stack>
        </Breakpoint>

        <Breakpoint lt="sm">
          <div className="ModaNetwork__header-container--mobile">
            <img
              className="ModaNetwork__header-image"
              src="/dist/public/stylist/moda_network_MOB1.jpg"
              alt="Moda Network header mobile"
            />

            <Stack space={4} className="ModaNetwork__text-container" alignItems="center">
              <Text treatment="h2" family="serif">
                Moda NETWORK
              </Text>
              <Stack space={0}>
                <Text>
                  The world's premier invitation-only network for stylists, interior decorators, and
                  costume designers.
                </Text>
              </Stack>
              <Text>
                Grow your business by joining Moda Network to recieve unique access and commissions
                to luxury brands and designers globally.
              </Text>
              <div className="ModaNetwork__button-wrapper">
                <Button
                  secondary
                  className="ModaNetwork__button-main--mobile"
                  onClick={() =>
                    handleClick(
                      'Moda Network Show Form Clicked',
                      'APPLY NOW',
                      'Banner Image Mobile'
                    )
                  }
                >
                  APPLY NOW
                </Button>
              </div>
            </Stack>
            <img
              className="ModaNetwork__image-mobile"
              src="/dist/public/stylist/moda_network_MOB2.jpg"
              alt="Moda Network mobile"
            />
          </div>
        </Breakpoint>

        <Constrain>
          <Stack
            space={5}
            className="ModaNetwork__section ModaNetwork__benefits-section"
            alignItems="center"
          >
            <Text treatment="h4" family="serif" className="ModaNetwork__benefits-title">
              What Are The Benefits Of Joining?
            </Text>
            <Stack space={2} className="ModaNetwork__benefits-text" alignItems="center">
              <Text>We offer competitive commissions or a private discount</Text>
              <Text>Access to consignment (US only)</Text>
              <Text>Dedicated point-of-contact for all of your shopping & styling needs</Text>
              <Text>Access to private sourcing and one-of-kind pieces</Text>
              <Text>Exclusive insider invitations</Text>
            </Stack>
            <div className="ModaNetwork__button">
              <Button
                secondary
                className="ModaNetwork__button-main"
                onClick={() =>
                  handleClick(
                    'Moda Network Show Form Clicked',
                    'APPLY',
                    'Benefits APPLY NOW button'
                  )
                }
              >
                APPLY NOW
              </Button>
            </div>
          </Stack>

          <Divider />

          <Stack space={5} className="ModaNetwork__section ModaNetwork__faq-section">
            <div className="ModaNetwork__section-title">FAQ</div>
            <Stack space={4}>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  How can I sign up to become an affiliate stylist on MODA Network?
                </Text>
                <Text color="ink" treatment="body1">
                  MODA Network is an invitation-only program. If you are interested in joining,
                  please fill out our application form. Our team will review your information and
                  reach out if you qualify for the program.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  What is MODA Network and who can join?
                </Text>
                <Text color="ink" treatment="body1">
                  MODA Network is a premier program tailored for stylists and costume designers. We
                  provide a unique service for you to grow your business, including competitive
                  commissions, consignment options, private sourcing, and access to top-tier luxury
                  brands. Our services are designed to elevate your product and streamline your
                  workflow, making shopping and styling for clients or events more efficient and
                  enjoyable.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  What are the benefits of joining MODA Network?
                </Text>
                <Text color="ink" treatment="body1">
                  As a member of MODA Network, you will receive competitive commissions or private
                  discounts, a dedicated point-of-contact for all your shopping and styling needs,
                  access to one-of-a-kind fine jewelry and rare vintage sourcing, and exclusive
                  event invitations. Those within the United States will also receive consignment
                  services.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  How does consignment work with MODA Network?
                </Text>
                <Text color="ink" treatment="body1">
                  Consignment within MODA Network allows you to curate impeccable edits for client
                  fittings, premieres, and special events without visiting a store. Please note that
                  consignment privileges are granted at the discretion of the MODA Network team and
                  are only available in the United States.
                </Text>
              </Stack>
              <Stack space={1}>
                <Text color="ink" family="serif" treatment="h5">
                  Can I place my own orders, or do I need your help each time?
                </Text>
                <Text color="ink" treatment="body1">
                  Absolutely, you can place your own orders! Just be sure to log into your affiliate
                  account when making a purchase, and you'll automatically earn your commission. The
                  commission will be processed once the order's return window has expired.
                </Text>
              </Stack>
            </Stack>
            <div className="ModaNetwork__button">
              <Button
                secondary
                className="ModaNetwork__button-main"
                onClick={() =>
                  handleClick('Moda Network Show Form Clicked', 'APPLY', 'FAQ APPLY NOW button')
                }
              >
                APPLY NOW
              </Button>
            </div>
          </Stack>
        </Constrain>

        <Breakpoint gt="sm">
          <div className="ModaNetwork__moda-private-container">
            <img
              className="ModaNetwork__image-main"
              src="/dist/public/stylist/moda_network_DT1.jpg"
              alt="Moda Network"
            />

            <Stack space={2}>
              <Stack space={2} className="ModaNetwork__text-main">
                <LogoModaPrivateIcon
                  className="ModaNetwork__logo-icon"
                  height="18px"
                  width="248px"
                />

                <Text treatment="h5" family="serif" className="ModaNetwork__title">
                  Our Premier Styling Program
                </Text>
                <Text treatment="body1">
                  Members of our private client program enjoy complimentary styling, bespoke
                  wardrobing, and special VIP programming as part of our by-invitation-only service.
                </Text>
              </Stack>
              <Button
                secondary
                className="ModaNetwork__moda-private-button"
                to={hrefFor.ModaPrivate()}
              >
                LEARN MORE
              </Button>
            </Stack>
          </div>
        </Breakpoint>

        <Breakpoint lt="sm">
          <div className="ModaNetwork__moda-private-container--mobile">
            <img
              className="ModaNetwork__image-main"
              src="/dist/public/stylist/moda_network_MOB3.jpg"
              alt="Moda Network mobile"
            />
            <Constrain>
              <Stack
                space={4}
                className="ModaNetwork__moda-private-text-container"
                alignItems="center"
              >
                <LogoModaPrivateIcon
                  className="ModaNetwork__logo-icon--mobile"
                  height="18px"
                  width="254px"
                />
                <Stack space={4}>
                  <Text treatment="h4" family="serif">
                    Your Personal Stylist
                  </Text>
                  <Text treatment="body1">
                    Receive complimentary access to best-in-class styling, wardrobing, and special
                    VIP programming when you join our coveted Private Client Program.
                  </Text>
                  <div className="ModaNetwork__button-wrapper">
                    <Button
                      secondary
                      className="ModaNetwork__button-main--mobile"
                      to={hrefFor.ModaPrivate()}
                    >
                      LEARN MORE
                    </Button>
                  </div>
                </Stack>
              </Stack>
              <Divider />
            </Constrain>
          </div>
        </Breakpoint>

        <Constrain>
          <Stack space={5} className="ModaNetwork__section ModaNetwork__shop-more-section">
            <div className="ModaNetwork__section-title">Shop MORE</div>
            <Stack space={1} alignItems="center" className="ModaNetwork__links">
              <Link to="/shop/laurens-closet" className="ModaNetwork__link">
                <Text color="ink" treatment="body1" family="serif">
                  LAUREN'S CLOSET: Discover LSD's favorite pieces
                </Text>
              </Link>
              <Link to="/new" className="ModaNetwork__link">
                <Text color="ink" treatment="body1" family="serif">
                  WHAT'S NEW: The Latest From Leading Designer Names
                </Text>
              </Link>
              <Link to="/shop/the-event-edit" className="ModaNetwork__link ModaNetwork__link--last">
                <Text color="ink" treatment="body1" family="serif">
                  THE EVENT EDIT: Exciting Plans? Find Something Fabulous To Wear Here
                </Text>
              </Link>
            </Stack>
          </Stack>
        </Constrain>
      </div>
      <ModaNetworkRequestModal show={showRequestModal} onClose={() => setShowRequestModal(false)} />
    </>
  );
};
